import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useAuth, roleHome } from "@/context/AuthContext";

const NAV = [
  { to: "/solutions", label: "Solutions" },
  { to: "/services", label: "Services" },
  { to: "/sectors", label: "Sectors" },
  { to: "/lending-network", label: "Lending network" },
  { to: "/buy-leads", label: "Buy leads" },
  { to: "/pay-per-call", label: "Live Calls" },
  { to: "/ai-systems", label: "AI Systems" },
  { to: "/activity", label: "Live", live: true },
  { to: "/pricing", label: "Pricing" },
];

export default function Header() {
  const [open, setOpen] = React.useState(false);
  const { user } = useAuth();
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/60 bg-white/95 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-logo">
            <BrandMark />
            <span className="text-[15px] font-semibold tracking-tight text-slate-900">Connexis<span className="text-primary">.</span></span>
          </Link>
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${isActive ? "text-slate-900 bg-slate-100" : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"}`
                }
              >
                {n.live && (
                  <span className="relative flex items-center justify-center size-1.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-full size-1.5 bg-emerald-500" />
                  </span>
                )}
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-2">
            {user ? (
              <Button data-testid="header-dashboard-btn" onClick={() => nav(roleHome(user.role))} className="rounded-full px-4 h-9">
                Open dashboard <ArrowUpRight className="size-4" />
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost" className="h-9 rounded-full" data-testid="header-login-btn">
                  <Link to="/login">Sign in</Link>
                </Button>
                <Button asChild data-testid="header-start-btn" className="rounded-full px-4 h-9 bg-slate-900 hover:bg-slate-800 gap-1.5">
                  <Link to="/start">Start now <ArrowUpRight className="size-3.5" /></Link>
                </Button>
              </>
            )}
          </div>
          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden pb-4 space-y-1" data-testid="mobile-menu">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className="block px-3 py-2 text-sm rounded-md text-slate-700 hover:bg-slate-50">
                {n.label}
              </NavLink>
            ))}
            <div className="flex gap-2 pt-2">
              {user ? (
                <Button className="flex-1 rounded-full" onClick={() => { setOpen(false); nav(roleHome(user.role)); }}>Dashboard</Button>
              ) : (
                <>
                  <Link to="/login" className="flex-1"><Button variant="outline" className="w-full rounded-full">Sign in</Button></Link>
                  <Link to="/start" className="flex-1"><Button className="w-full rounded-full">Start <ArrowUpRight className="size-3.5 ml-1" /></Button></Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

function BrandMark() {
  return (
    <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-slate-900 text-white">
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 7h7M4 12h12M4 17h7" strokeLinecap="round" />
        <circle cx="18" cy="7" r="2.2" fill="#0055FF" stroke="none" />
        <circle cx="18" cy="17" r="2.2" fill="#0055FF" stroke="none" />
      </svg>
    </span>
  );
}
